import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  CellClickedEvent,
  GetRowIdParams,
  GridApi,
  GridReadyEvent,
  RowSelectionOptions,
  SelectionChangedEvent,
  SideBarDef
} from 'ag-grid-enterprise'
import { AgGridReact } from 'ag-grid-react'
import { useTranslation } from 'react-i18next'
import CustomAgGrid from '../../../shared/components/custom/Grid/CustomAgGrid'
import agGridTheme from '../../../config/agGrid/theme'
import { getMatchId, isAcceptanceStale } from '../utils/reconciliationHelpers'
import { Match } from '../types'
import { getReconciliationColumnDefs } from './reconciliationColumnDefs'
import { ReconciliationGridContext } from './reconciliationCellRenderers'
import ReconciliationDetailPanel from './ReconciliationDetailPanel'
import ReconciliationEntryModal from './ReconciliationEntryModal'

// 44px (not the usual 36) so the amount cells can stack the reporting-currency figure and the
// original ledger-currency figure on two lines without clipping.
const ROW_HEIGHT = 44

const noColumnBorderTheme = agGridTheme.withParams({ columnBorder: false, rowHeight: ROW_HEIGHT })

const rowSelection: RowSelectionOptions<Match> = {
  mode: 'multiRow',
  checkboxes: true,
  headerCheckbox: true,
  // "select all" only touches rows left by the active filters, not the whole dataset
  selectAll: 'filtered',
  enableClickSelection: false
}

const staleRowStyle = { backgroundColor: '#fff7e6' }

const sideBar: SideBarDef = {
  toolPanels: [
    {
      id: 'columns',
      labelDefault: 'Columns',
      labelKey: 'columns',
      iconKey: 'columns',
      toolPanel: 'agColumnsToolPanel',
      toolPanelParams: {
        suppressRowGroups: true,
        suppressValues: true,
        suppressPivots: true,
        suppressPivotMode: true
      }
    }
  ],
  defaultToolPanel: ''
}

interface ReconciliationTableProps {
  matches: Match[]
  companyNameById: Map<string, string>
  // Approver id (Match.accepted.acceptedBy) -> display name, merged across the group's companies.
  userNameById: Map<number, string>
  // ISO code of the group's reporting currency (parent company's `currency`).
  groupCurrency: string
  onSelectionChange: (rows: Match[]) => void
  onApproveEntry: (match: Match) => void
  onRevokeEntry: (match: Match) => void
  // The rows left after the grid's column filters (ignores pagination) — drives the summary cards.
  onFilteredRowsChange?: (rows: Match[]) => void
  // Applied once as the initial date-column filter (YYYY-MM-DD). The user can clear it afterwards.
  defaultDateRange?: { from: string; to: string }
  height?: number | string
  loading?: boolean
}

// Ref forwards the underlying AgGridReact so the page can call api.deselectAll() after a mutation.
const ReconciliationTable = forwardRef<AgGridReact<Match>, ReconciliationTableProps>(
  (
    {
      matches,
      companyNameById,
      userNameById,
      groupCurrency,
      onSelectionChange,
      onApproveEntry,
      onRevokeEntry,
      onFilteredRowsChange,
      defaultDateRange,
      height = 700,
      loading
    },
    ref
  ) => {
    const { t } = useTranslation()
    const [selectedMatch, setSelectedMatch] = useState<Match | null>(null)
    const gridApiRef = useRef<GridApi<Match> | null>(null)
    const defaultFilterAppliedRef = useRef(false)

    // Seed the date filter with the group parent's ongoing fiscal year, once, as soon as both
    // the grid and the range are available (fiscal year loads async).
    const applyDefaultDateFilter = useCallback(() => {
      if (defaultFilterAppliedRef.current || !defaultDateRange || !gridApiRef.current) return
      defaultFilterAppliedRef.current = true
      gridApiRef.current.setFilterModel({
        date: {
          filterType: 'date',
          type: 'inRange',
          dateFrom: defaultDateRange.from,
          dateTo: defaultDateRange.to
        }
      })
    }, [defaultDateRange])

    useEffect(() => {
      applyDefaultDateFilter()
    }, [applyDefaultDateFilter])

    // Report the post-filter row set (all pages) so the page can aggregate the summary cards
    // over exactly what the grid's filters leave visible.
    const emitFilteredRows = useCallback(() => {
      const api = gridApiRef.current
      if (!api || !onFilteredRowsChange) return
      const rows: Match[] = []
      api.forEachNodeAfterFilter(node => {
        if (node.data && !node.detail) rows.push(node.data)
      })
      onFilteredRowsChange(rows)
    }, [onFilteredRowsChange])

    const handleGridReady = (event: GridReadyEvent<Match>) => {
      gridApiRef.current = event.api
      applyDefaultDateFilter()
    }

    const handleCellClicked = (event: CellClickedEvent<Match>) => {
      const target = event.event?.target as HTMLElement | null
      if (target?.closest('.ag-group-expanded, .ag-group-contracted')) return
      if (event.column.getColId() === 'actions') return
      if (event.data) setSelectedMatch(event.data)
    }

    const gridContext = useMemo<ReconciliationGridContext>(
      () => ({ companyNameById, userNameById, groupCurrency, onApproveEntry, onRevokeEntry }),
      [companyNameById, userNameById, groupCurrency, onApproveEntry, onRevokeEntry]
    )

    const columnDefs = useMemo(() => getReconciliationColumnDefs(t, groupCurrency), [t, groupCurrency])

    const defaultColDef = useMemo(
      () => ({
        resizable: true,
        sortable: true,
        filter: true,
        floatingFilter: true,
        suppressHeaderMenuButton: true
      }),
      []
    )

    return (
      <div style={{ height }}>
        <CustomAgGrid
          ref={ref}
          persistStateId="icReconciliation"
          theme={noColumnBorderTheme}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowData={matches}
          loading={loading}
          sideBar={sideBar}
          getRowId={(params: GetRowIdParams<Match>) => getMatchId(params.data)}
          onGridReady={handleGridReady}
          onModelUpdated={emitFilteredRows}
          rowHeight={ROW_HEIGHT}
          rowSelection={rowSelection}
          selectionColumnDef={{ pinned: 'left', width: 44, resizable: false, lockPosition: true }}
          onSelectionChanged={(event: SelectionChangedEvent<Match>) => onSelectionChange(event.api.getSelectedRows())}
          getRowStyle={params => (params.data && isAcceptanceStale(params.data) ? staleRowStyle : undefined)}
          masterDetail
          detailRowAutoHeight
          detailCellRenderer={ReconciliationDetailPanel}
          onCellClicked={handleCellClicked}
          context={gridContext}
          pagination
          paginationPageSize={20}
          paginationPageSizeSelector={[20, 50, 100]}
        />
        <ReconciliationEntryModal
          match={selectedMatch}
          companyNameById={companyNameById}
          groupCurrency={groupCurrency}
          onClose={() => setSelectedMatch(null)}
        />
      </div>
    )
  }
)

ReconciliationTable.displayName = 'ReconciliationTable'

export default ReconciliationTable
